import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const AuthTestEnhanced = () => {
  const {
    user,
    userType,
    login: loginFunc,
    register: registerFunc,
    logout,
    refreshToken,
    sendOTP,
    sendEmailOTP,
    verifyOTP,
    verifyEmailOTP,
    resendOTP,
    resetOTP,
    sendDefaultOTPAdmin,
    loading,
    authLoading,
    authError
  } = useAuth();
  
  // Simple loading state
  const authLoadingState = {
    login: authLoading,
    register: authLoading,
    otp: authLoading,
    refresh: authLoading
  };
  
  const isAuthenticated = !!user;
  
  // Simple validation functions
  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const validatePhoneFormat = (phone) => {
    const phoneRegex = /^\+91\s\d{10}$/;
    return phoneRegex.test(phone);
  };
  
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  };
  
  // Wrapper functions to match expected interface
  const login = async (credentials) => {
    const result = await loginFunc(credentials);
    return result;
  };
  
  const register = async (userData) => {
    const result = await registerFunc(userData);
    return result;
  };

  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testPhone, setTestPhone] = useState('+91 9876543210');
  const [testPassword, setTestPassword] = useState('password123');
  const [testOTP, setTestOTP] = useState('123456');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    try {
      setError(null);
      const response = await login({
        email: testEmail,
        password: testPassword,
        userType: 'user'
      });
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRegister = async () => {
    try {
      setError(null);
      const response = await register({
        name: 'Test User',
        email: testEmail,
        password: testPassword,
        phone: testPhone
      });
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = async () => {
    try {
      setError(null);
      await logout();
      setResult({ success: true, message: 'Logged out successfully' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefreshToken = async () => {
    try {
      setError(null);
      const token = await refreshToken();
      setResult({ success: true, token });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendOTP = async () => {
    try {
      setError(null);
      const response = await sendOTP(testPhone);
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendEmailOTP = async () => {
    try {
      setError(null);
      const response = await sendEmailOTP(testEmail);
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setError(null);
      const response = await verifyOTP(testOTP);
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleVerifyEmailOTP = async () => {
    try {
      setError(null);
      const response = await verifyEmailOTP(testOTP);
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResendOTP = async () => {
    try {
      setError(null);
      const response = await resendOTP();
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendDefaultOTP = async () => {
    try {
      setError(null);
      const response = await sendDefaultOTPAdmin(testEmail);
      setResult(response);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleValidateEmail = () => {
    const isValid = validateEmailFormat(testEmail);
    setResult({ success: true, valid: isValid, email: testEmail });
  };

  const handleValidatePhone = () => {
    const isValid = validatePhoneFormat(testPhone);
    setResult({ success: true, valid: isValid, phone: testPhone });
  };

  const handleCheckToken = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      const expired = isTokenExpired(token);
      setResult({ success: true, token, expired });
    } else {
      setResult({ success: false, message: 'No token found' });
    }
  };

  const handleReset = () => {
    resetOTP();
    setResult(null);
    setError(null);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto mt-10">
      <h2 className="text-2xl font-bold mb-6 text-center">Enhanced Authentication Test</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
          <input
            type="password"
            value={testPassword}
            onChange={(e) => setTestPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">OTP</label>
          <input
            type="text"
            value={testOTP}
            onChange={(e) => setTestOTP(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            maxLength="6"
          />
        </div>
      </div>

      {/* Authentication Status */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">Authentication Status</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="font-medium">Authenticated:</span> {isAuthenticated ? 'Yes' : 'No'}</div>
          <div><span className="font-medium">User Type:</span> {userType}</div>
          <div><span className="font-medium">User:</span> {user ? user.email : 'None'}</div>
          <div><span className="font-medium">Loading:</span> {authLoading ? 'Yes' : 'No'}</div>
        </div>
      </div>

      {/* Loading States */}
      <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
        <h3 className="font-medium text-yellow-800 mb-2">Loading States</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="font-medium">Login:</span> {authLoadingState.login ? 'Loading...' : 'Idle'}</div>
          <div><span className="font-medium">Register:</span> {authLoadingState.register ? 'Loading...' : 'Idle'}</div>
          <div><span className="font-medium">OTP:</span> {authLoadingState.otp ? 'Loading...' : 'Idle'}</div>
          <div><span className="font-medium">Refresh:</span> {authLoadingState.refresh ? 'Loading...' : 'Idle'}</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <button
          onClick={handleLogin}
          disabled={authLoadingState.login}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          Login
        </button>
        
        <button
          onClick={handleRegister}
          disabled={authLoadingState.register}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
        >
          Register
        </button>
        
        <button
          onClick={handleLogout}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          Logout
        </button>
        
        <button
          onClick={handleRefreshToken}
          disabled={authLoadingState.refresh}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
        >
          Refresh Token
        </button>
        
        <button
          onClick={handleSendOTP}
          disabled={authLoadingState.otp}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Send Phone OTP
        </button>
        
        <button
          onClick={handleSendEmailOTP}
          disabled={authLoadingState.otp}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
        >
          Send Email OTP
        </button>
        
        <button
          onClick={handleSendDefaultOTP}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
        >
          Send Default OTP
        </button>
        
        <button
          onClick={handleResendOTP}
          disabled={authLoadingState.otp}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
        >
          Resend OTP
        </button>
        
        <button
          onClick={handleVerifyOTP}
          disabled={authLoadingState.otp}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
        >
          Verify Phone OTP
        </button>
        
        <button
          onClick={handleVerifyEmailOTP}
          disabled={authLoadingState.otp}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
        >
          Verify Email OTP
        </button>
        
        <button
          onClick={handleValidateEmail}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Validate Email
        </button>
        
        <button
          onClick={handleValidatePhone}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Validate Phone
        </button>
        
        <button
          onClick={handleCheckToken}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Check Token
        </button>
        
        <button
          onClick={handleReset}
          className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Reset
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
          <strong>Result:</strong>
          <pre className="mt-2 text-xs overflow-auto max-h-40">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}

      {/* Auth Error Display */}
      {authError && (
        <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded-md">
          <strong>Auth Error:</strong> {authError}
        </div>
      )}

      {/* Token Info */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-800 mb-2">Token Information</h3>
        <div className="text-sm">
          <div><span className="font-medium">Access Token:</span> {localStorage.getItem('auth_token') ? 'Present' : 'None'}</div>
          <div><span className="font-medium">Refresh Token:</span> {localStorage.getItem('refresh_token') ? 'Present' : 'None'}</div>
          <div><span className="font-medium">User Data:</span> {localStorage.getItem('mgx_user') ? 'Present' : 'None'}</div>
        </div>
      </div>
    </div>
  );
};

export default AuthTestEnhanced;